// Production → Inventory count. Materials MIC session + recipe cost
// posting. Desk Inventory keeps catalog / on-hand / receive.
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { canEditManufacturingCatalogCode } from "@/lib/manufacturing/modelDeleteAccess";
import { listManufacturingPlants, type MfgPlant } from "@/lib/manufacturingClient";
import {
  ghostBtnClass,
  inputClass,
  mfgAccentBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingGateBanner, ManufacturingToast } from "./ManufacturingGateBanner";
import { ManufacturingInventoryCountHost } from "./ManufacturingInventoryCountHost";
import { ManufacturingInventoryCountOpenChooser } from "./ManufacturingInventoryCountOpenChooser";
import { ManufacturingTabbedSection } from "./ManufacturingTabbedSection";
import { RecipeCountBody } from "./RecipeCountBody";
import { RecipeCountDictatePanel } from "./RecipeCountDictatePanel";
import { useAutoDismissToast } from "./useAutoDismissToast";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingInventoryCountEntry } from "./useManufacturingInventoryCountEntry";
import { useManufacturingRecipesList } from "./useManufacturingRecipesList";

type CountTab = "materials" | "recipes";

const COUNT_TABS: Array<{
  id: CountTab;
  label: string;
  description: string;
}> = [
  {
    id: "materials",
    label: "Materials",
    description:
      "Full-screen floor flow: start or resume a plant session, count lines (search + qty), then Submit to reconcile on-hand. Exit keeps a partial count; discard archives it.",
  },
  {
    id: "recipes",
    label: "Recipes",
    description:
      "Post an opening balance, build, or adjustment against a recipe (subassembly on-hand).",
  },
];

export function ManufacturingInventoryCountPanel() {
  const auth = useAuth();
  const gate = useManufacturingEntitlementGate();
  const canReviewProposals =
    auth?.status === "ready" && canEditManufacturingCatalogCode((...r) => auth.hasRole(...r));
  const recipes = useManufacturingRecipesList({ enabled: gate.enabled });

  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [plantId, setPlantId] = useState("");
  const [plantsBusy, setPlantsBusy] = useState(false);
  const [plantsError, setPlantsError] = useState<string | null>(null);
  const [tab, setTab] = useState<CountTab>("materials");
  const [toast, setToast] = useState<string | null>(null);
  useAutoDismissToast(toast, () => setToast(null));
  const entry = useManufacturingInventoryCountEntry({ enabled: gate.enabled, plantId });

  const loadPlants = useCallback(async () => {
    setPlantsBusy(true);
    setPlantsError(null);
    try {
      const res = await listManufacturingPlants();
      if (!res.ok || !res.data.ok) {
        setPlantsError("error" in res ? String(res.error) : "Failed to load plants");
        return;
      }
      const next = Array.isArray(res.data.plants) ? res.data.plants : [];
      setPlants(next);
      setPlantId((prev) => {
        if (prev && next.some((p) => p.id === prev)) return prev;
        const def = next.find((p) => p.is_default) ?? next[0];
        return def?.id ?? "";
      });
    } finally {
      setPlantsBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!gate.enabled) return;
    void loadPlants();
  }, [gate.enabled, loadPlants]);

  return (
    <div className="w-full space-y-4 text-left">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">
            Inventory count
          </h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            Open a plant count session, enter quantities, then submit to post adjustments.
            Exit without submitting keeps the partial count.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
            Plant
            <select
              className={inputClass}
              value={plantId}
              onChange={(e) => {
                setPlantId(e.target.value);
              }}
              disabled={plantsBusy || plants.length === 0}
            >
              {plants.length === 0 ? <option value="">No plants</option> : null}
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.plant_code ? `${p.plant_code} · ${p.name}` : p.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={plantsBusy}
            onClick={() => void loadPlants()}
          >
            Refresh
          </button>
        </div>
      </div>

      <ManufacturingGateBanner gate={gate} />
      {plantsError ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {plantsError}
        </p>
      ) : null}
      <ManufacturingToast message={toast} />

      <ManufacturingTabbedSection<CountTab>
        tabs={COUNT_TABS}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === "materials" ? (
          <div className="space-y-2">
            <button
              type="button"
              className={mfgAccentBtnClass}
              disabled={!gate.enabled || !plantId || entry.countOpen || entry.startBusy}
              onClick={() => void entry.startOrChoose()}
              title="Open or manage the materials inventory count session"
            >
              {entry.countOpen
                ? "Count in progress…"
                : entry.openDraft
                  ? "Resume / manage materials count"
                  : "Start materials count"}
            </button>
            {entry.openDraft && !entry.countOpen ? (
              <p className="text-xs text-gray-600 dark:text-zinc-400">
                In progress · {entry.openDraft.counted_line_count} of {entry.openDraft.line_count}{" "}
                counted. Exit saved this session; submit to post adjustments or discard to archive.
              </p>
            ) : null}
            {entry.entryError ? (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {entry.entryError}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            <RecipeCountDictatePanel
              recipes={recipes.recipes}
              enabled={gate.enabled}
              onPosted={(msg) => setToast(msg)}
            />
            <RecipeCountBody
              recipes={recipes.recipes}
              recipesLoading={recipes.loading}
              onPosted={(msg) => setToast(msg)}
            />
          </div>
        )}
      </ManufacturingTabbedSection>

      {plantId ? (
        <ManufacturingInventoryCountHost
          key={plantId}
          open={entry.countOpen}
          enabled={gate.enabled}
          plantId={plantId}
          canReviewProposals={canReviewProposals}
          onClose={entry.closeCount}
          onSessionClosed={(summary) => setToast(summary)}
        />
      ) : null}

      <ManufacturingInventoryCountOpenChooser
        open={entry.chooserOpen}
        busy={entry.startBusy}
        draft={entry.openDraft}
        onResume={entry.resume}
        onStartNew={() => {
          void (async () => {
            const r = await entry.startNew();
            if (r.ok) setToast("Previous count archived — starting a new session");
          })();
        }}
        onDiscard={() => {
          void (async () => {
            const r = await entry.discard();
            if (r.ok) setToast("Count archived — no stock adjustments posted");
          })();
        }}
        onCancel={entry.cancelChooser}
      />

      {!plantId && gate.enabled && !plantsBusy ? (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Select a plant to run a materials count session.
        </p>
      ) : null}
    </div>
  );
}
